/**
 * @license
 * @copyright Copyright Motili Inc., 2019 All Rights Reserved
 */

import _, { round } from 'lodash';
import moment from 'moment';
import isFinite from 'lodash/isFinite';

import Config from 'Config';
import { getCountryFromCurrency } from 'common/utils/Utils';
import { isProductAvailable } from 'common/utils/Validator';
import {
    calculate,
    calculateMarginPercentage,
} from 'common/services/ProductService';
import {
    DRAFT_STATUS,
    LINE_ITEM_STATUSES,
    ORDER_STATUS,
    PRODUCT_CATEGORY,
    QUOTE_STATUS,
    WORK_ORDER_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { isMotiliOwner } from 'common/services/AuthService';

export function allItemsContractorSuppliedOrFees(lineItems) {
    // eslint-disable-line class-methods-use-this
    let hasOnlySuppliedItemsOrFees = true;
    lineItems?.forEach(item => {
        if (item.product?.productCategoryId) {
            if (
                item.product.productCategoryId !== 'LABOR' &&
                item.product.productCategoryId !== 'SUPPLIED' &&
                item.product.productCategoryId !== 'FEE' &&
                item.product.productSubCategoryId !== 'FEE'
            ) {
                hasOnlySuppliedItemsOrFees = false;
            }
        } else if (
            item.productCategoryId !== 'LABOR' &&
            item.productCategoryId !== 'SUPPLIED' &&
            item.productCategoryId !== 'FEE' &&
            item.productSubCategoryId !== 'FEE'
        ) {
            hasOnlySuppliedItemsOrFees = false;
        }
    });
    return hasOnlySuppliedItemsOrFees;
}

export function getLineItemPrice(lineItem) {
    return lineItem.price?.adjustedPrice ?? 0;
}

export function getLineItemPriceDisplay(lineItem) {
    const price = getLineItemPrice(lineItem);
    return lineItem.adjustment || lineItem.isReturned ? -price : price;
}

export function getLineItemCost(lineItem) {
    return lineItem.cost?.cost ?? 0;
}

export function getLineItemCostDisplay(lineItem) {
    const cost = getLineItemCost(lineItem);
    return lineItem.adjustment || lineItem.isReturned ? -cost : cost;
}

export function getLineItemPayout(lineItem) {
    return lineItem.payout.adjustedPayout;
}

export function getLineItemSubtotalDisplay(lineItem, value = 0) {
    const quantity = Math.abs(lineItem.quantity ?? 0);
    const subTotal = quantity * value;
    return lineItem.adjustment || lineItem.isReturned ? -subTotal : subTotal;
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceSubTotal(lineItems) {
    return lineItems
        .filter(lineItem => lineItem.price)
        .filter(
            lineItem =>
                !isShipping(lineItem) &&
                !isTaxes(lineItem) &&
                !isBundleProduct(lineItem)
        )
        .reduce(
            (acc, next) =>
                acc + Math.abs(next.quantity) * (next.price.adjustedPrice || 0),
            0
        );
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceSubTotalIncludeShippingLineItems(lineItems) {
    return lineItems
        .filter(lineItem => lineItem.price)
        .filter(lineItem => !isBundleProduct(lineItem.price))
        .filter(lineItem => !isTaxes(lineItem))
        .reduce(
            (acc, next) =>
                acc + Math.abs(next.quantity) * (next.price.adjustedPrice || 0),
            0
        );
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceShipping(lineItems) {
    return lineItems
        .filter(lineItem => isShipping(lineItem))
        .reduce(
            (acc, next) =>
                acc +
                Math.abs(next.quantity - (next.returnQuantity || 0)) *
                    (next.price?.adjustedPrice || 0),
            0
        );
}

export function sumPriceShippingReturned(lineItems) {
    return lineItems
        .filter(lineItem => isShipping(lineItem))
        .filter(lineItem => isReturned(lineItem))
        .reduce(
            (acc, next) =>
                acc +
                Math.abs(next.returnQuantity) *
                    (next.price?.adjustedPrice || 0),
            0
        );
}

/**
 * calculated from
 * - adjustedPriceTax in the line item
 * - adjustedPrice in the line item category TAXES
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceTax(lineItems) {
    return (
        lineItems
            .filter(
                lineItem => !isTaxes(lineItem) && !isBundleProduct(lineItem)
            )
            .reduce(
                (acc, next) =>
                    acc +
                    Math.abs(next.quantity) *
                        (next.price?.adjustedPriceTax || 0),
                0
            ) +
        lineItems
            .filter(lineItem => isTaxes(lineItem) && !isBundleProduct(lineItem))
            .reduce(
                (acc, next) =>
                    acc +
                    Math.abs(next.quantity) * (next.price?.adjustedPrice || 0),
                0
            )
    );
}

/**
 * Summary total price included tax
 *
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceTotal(lineItems) {
    return lineItems
        .filter(lineItem => lineItem.price)
        .filter(lineItem => !isBundleProduct(lineItem))
        .reduce(
            (acc, next) =>
                acc +
                Math.abs(next.quantity) *
                    (next.price.adjustedPriceIncludingTax ||
                        next.price.adjustedPrice ||
                        0),
            0
        );
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {T}
 */
export function sumPayoutTotal(lineItems) {
    return lineItems
        .filter(lineItem => lineItem.payout)
        .filter(lineItem => !isTaxes(lineItem))
        .filter(lineItem => !isBundleProduct(lineItem))
        .reduce(
            (acc, next) =>
                acc +
                Math.abs(next.quantity) * (next.payout.adjustedPayout || 0),
            0
        );
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {Boolean}
 */
export function includesOrderedEquipment(lineItems) {
    return !!_.find(lineItems, isOrderedItem);
}

/**
 * Check line item that it is ordered product
 * @param lineItem
 * @returns {boolean}
 */
export function isOrderedItem(lineItem) {
    if (!lineItem) {
        return false;
    }
    const categories = ['EQUIPMENT', 'PART', 'SHIPPING'];
    if (categories.includes(lineItem.productCategoryId)) {
        return true;
    }
    if (
        lineItem.productCategoryId === 'CUSTOM' &&
        categories.includes(lineItem.productSubCategoryId)
    ) {
        return true;
    }
    return isMigratedOrderedItem(lineItem);
}

export function hasSuppliedItem(products) {
    let _hasSuppliedItem = false;
    products.forEach(product => {
        if (product.product.productCategoryId === 'SUPPLIED') {
            _hasSuppliedItem = true;
        }
    });
    return _hasSuppliedItem;
}

export function isEquipmentOrPart(lineItem) {
    if (!lineItem) {
        return false;
    }
    const categories = ['EQUIPMENT', 'PART'];
    if (categories.includes(lineItem.productCategoryId)) {
        return true;
    }
    return (
        lineItem.productCategoryId === 'CUSTOM' &&
        categories.includes(lineItem.productSubCategoryId)
    );
}

export function hasAssetTagLineItem(lineItems) {
    return !!lineItems.find(
        lineItem =>
            lineItem.sku.includes('LFEE-V3ASTTGFE') &&
            lineItem.statusId !== 'COMPLETED'
    );
}

/**
 * Determines if at least one product or line item in an array has a supplied vendor cost
 * @param {Object[]} products
 * @returns {Boolean}
 */
export function hasSuppliedVendorCostItem(products) {
    for (let i = 0; i < products.length; i += 1) {
        if (products[i].product) {
            if (products[i].product.hasSuppliedVendorCost) {
                return true;
            }
        }
        if (products[i].hasSuppliedVendorCost) {
            return true;
        }
    }
    return false;
}

/**
 *
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function isMigratedOrderedItem(lineItem) {
    if (lineItem.productCategoryId === 'MIGRATED') {
        let _notOrderedLineItem = lineItem.sku.substring(0, 4) === 'SRV-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 4) === 'SKU-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 6) === 'HVCOM-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.substring(0, 6) === 'HVAC-';
        if (_notOrderedLineItem) return false;
        _notOrderedLineItem = lineItem.sku.match(/\d\./);
        return !_notOrderedLineItem;
    }
    return false;
}

export function groupLineItemsByBundle(lineItems) {
    // bundle lineItems and children
    const bundles = lineItems
        .filter(item => item.productCategoryId === 'BUNDLE')
        .map(bd => ({
            ...bd,
            lineItems: lineItems.filter(
                li =>
                    li.productCategoryId !== 'BUNDLE' &&
                    li.bundleJobLineItemId &&
                    li.bundleJobLineItemId === bd.id
            ),
        }));
    // normal lineItems
    const items = lineItems.filter(
        li => li.productCategoryId !== 'BUNDLE' && !li.bundleJobLineItemId
    );
    return _.concat(bundles, items);
}

export function getLineItemLabel(lineItem) {
    return lineItem.productCategoryId === 'CUSTOM'
        ? lineItem.productSubCategoryId
        : lineItem.productCategoryId;
}

export function getLineItemLabelColor(label) {
    switch (label) {
        case PRODUCT_CATEGORY.EQUIPMENT: {
            return Config.color.red;
        }
        case PRODUCT_CATEGORY.PART: {
            return Config.color.yellow;
        }
        case PRODUCT_CATEGORY.PACKAGES: {
            return Config.color.green;
        }
        case PRODUCT_CATEGORY.SUPPLIED: {
            return Config.color.purple;
        }
        case PRODUCT_CATEGORY.SHIPPING: {
            return Config.color.motiliDarkBlue;
        }
        case PRODUCT_CATEGORY.FEE: {
            return Config.color.motiliDarkBlue;
        }
        case PRODUCT_CATEGORY.LABOR: {
            return Config.color.vividBlue;
        }
        case PRODUCT_CATEGORY.CUSTOM: {
            return Config.color.motiliDarkBlue;
        }
        case PRODUCT_CATEGORY.WARRANTY: {
            return Config.color.motiliLightBlue;
        }
        default: {
            return Config.color.motiliDarkBlue;
        }
    }
}

export function getLineItemStatusColor(status) {
    switch (status) {
        case LINE_ITEM_STATUSES.APPROVED: {
            return Config.color.vividBlue;
        }
        case LINE_ITEM_STATUSES.CREATED: {
            return Config.color.mediumGray;
        }
        case LINE_ITEM_STATUSES.COMPLETED: {
            return Config.color.green;
        }
        case LINE_ITEM_STATUSES.RETURNING: {
            return Config.color.orange;
        }
        case LINE_ITEM_STATUSES.RETURNED:
        case LINE_ITEM_STATUSES.PARTIALLY_RETURNED: {
            return Config.color.red;
        }
        default: {
            return undefined;
        }
    }
}

function getCategorySortOrder(category, alphabet = true) {
    if (alphabet) {
        return category;
    }
    switch (category) {
        case 'EQUIPMENT':
            return 0;
        case 'PART':
            return 1;
        case 'PACKAGES':
            return 2;
        case 'LABOR':
            return 3;
        case 'SUPPLIED':
            return 4;
        case 'FEE':
            return 5;
        case 'CUSTOM':
            return 6;
        case 'SHIPPING':
            return 7;
        default:
            return 8;
    }
}

export function sortLineItems(lineItems) {
    return _.chain(lineItems)
        .sortBy(li => `${getCategorySortOrder(getLineItemLabel(li))}:${li.sku}`)
        .value();
}

export function getLineItemOrder(lineItems, displayBundle = true) {
    if (displayBundle) {
        let lineItemOrder = [];
        const bundleItems = groupLineItemsByBundle(lineItems);
        const bundles = bundleItems.filter(
            item => item.productCategoryId === 'BUNDLE'
        );
        if (_.size(bundles) > 0) {
            _.forEach(bundles, b => {
                if (_.size(b.lineItems) > 0) {
                    lineItemOrder.push(sortLineItems(b.lineItems));
                }
            });
        }
        const items = bundleItems.filter(v => v.productCategoryId !== 'BUNDLE');
        if (_.size(items) > 0) {
            lineItemOrder.push(sortLineItems(items));
        }
        lineItemOrder = _.flatten(lineItemOrder);
        lineItemOrder = _.map(lineItemOrder, li => li.id);
        return lineItemOrder;
    }

    return _.map(sortLineItems(lineItems), li => li.id);
}

/**
 *
 * @param {Object[]} lineItems
 * @returns {Number}
 */
export function sumPriceReturn(lineItems) {
    return lineItems
        .filter(lineItem => isReturned(lineItem))
        .filter(lineItem => !isBundleProduct(lineItem))
        .filter(lineItem => lineItem.price)
        .reduce(
            (acc, next) =>
                acc +
                (next.returnPrice
                    ? next.returnPrice
                    : Math.abs(next.returnQuantity) *
                      (next.price.adjustedPrice || 0)),
            0
        );
}

export const hasPartLineItems = lineItems =>
    lineItems.some(lineItem => isPart(lineItem));

export function isEquipment(lineItem) {
    return (
        lineItem?.productCategoryId === PRODUCT_CATEGORY.EQUIPMENT ||
        (lineItem?.productCategoryId === PRODUCT_CATEGORY.CUSTOM &&
            lineItem?.productSubCategoryId === PRODUCT_CATEGORY.EQUIPMENT)
    );
}

export function isPart(lineItem) {
    return (
        lineItem?.productCategoryId === PRODUCT_CATEGORY.PART ||
        (lineItem?.productCategoryId === PRODUCT_CATEGORY.CUSTOM &&
            lineItem?.productSubCategoryId === PRODUCT_CATEGORY.PART)
    );
}

export function isShipping(lineItem) {
    if (lineItem.productCategoryId === 'SHIPPING') {
        return true;
    }
    if (lineItem.productCategoryId === 'CUSTOM') {
        if (lineItem.productSubCategoryId === 'SHIPPING') {
            return true;
        }
    }
    return false;
}

/**
 * @param {Object} lineItem
 * @return {boolean}
 */
export function isTaxes(lineItem) {
    return !!(
        lineItem.productCategoryId === 'SUPPLIED' &&
        lineItem.productSubCategoryId === 'TAXES'
    );
}
export function isBundleProduct(lineItem) {
    if (!lineItem) {
        return false;
    }
    return !!lineItem.bundleJobLineItemId;
}

export function isProductBundle(lineItem) {
    if (!lineItem) {
        return false;
    }
    return lineItem.productCategoryId === 'BUNDLE';
}

/**
 *
 * @param {Object[]} lineItems
 * @return {Number}
 */
export function sumPayoutLineItemTax(lineItems) {
    return lineItems
        .filter(lineItem => isTaxes(lineItem))
        .reduce(
            (acc, next) =>
                acc +
                Math.abs(next.quantity) * (next.payout.adjustedPayout || 0),
            0
        );
}

export function isFeeOfReturnSKU(sku) {
    return /(-RESTKV1$|-RESHPFRTV1$)/.test(sku);
}

export function isMitigationFee(lineItem) {
    return isFee(lineItem) && /(-MTGN$)/.test(lineItem.sku);
}

export function isOrderedFreightChargesFee(lineItem) {
    return (
        lineItem.productCategoryId === 'SHIPPING' &&
        lineItem.productSubCategoryId === 'FREIGHT' &&
        /(-SHPFRTV3$)/.test(lineItem.sku)
    );
}

export function isFee(lineItem) {
    return (
        lineItem.productCategoryId === 'CUSTOM' &&
        lineItem.productSubCategoryId === 'FEE'
    );
}

export function isReturned(lineItem) {
    return lineItem.returnQuantity > 0;
}

/**
 *
 * @param {array} originalLineItems
 * @param {array} lineItems
 * @return {boolean}
 */
export function isLineItemsEdited(originalLineItems = [], lineItems = []) {
    if (originalLineItems.length !== lineItems.length) {
        return true;
    }
    return originalLineItems.some(org => {
        const lineItem = lineItems.find(li => li.id === org.id);
        return !(lineItem && org.updated === lineItem.updated);
    });
}

export async function getFinanceUpdateLineItems(document) {
    const { originalLineItems, lineItems } = document;
    const updateFields = [
        'id',
        'sku',
        'productId',
        'suppliedDescription',
        'suppliedVendorCost',
        'suppliedPayout',
        'quantity',
        'serialNumbers',
        'cost',
        'payout',
        'price',
    ];

    const getRemoveLineItem = () => {
        const removeLineItems = _.filter(
            originalLineItems,
            oli => !_.find(lineItems, li => oli.id === li.id)
        );
        if (removeLineItems.length > 0) {
            return _.map(removeLineItems, li => _.pick(li, ['id']));
        }
    };
    const getUpdateLineItems = () => {
        const updateLineItems = _.filter(lineItems, li => {
            const originalLineItem = _.find(
                originalLineItems,
                oli => oli.id === li.id
            );
            return originalLineItem && originalLineItem.updated !== li.updated;
        });

        if (updateLineItems.length > 0) {
            return _.map(updateLineItems, li => _.pick(li, updateFields));
        }
    };
    const getAddLineItems = () => {
        const addLineItems = _.filter(
            lineItems,
            li => typeof li.id === 'string'
        );

        if (addLineItems.length > 0) {
            return _.map(addLineItems, li => _.pick(li, updateFields));
        }
    };

    const processLineItems = await Promise.all([
        getRemoveLineItem(),
        getUpdateLineItems(),
        getAddLineItems(),
    ]);
    return {
        removeLineItems: processLineItems[0],
        updateLineItems: processLineItems[1],
        addLineItems: processLineItems[2],
    };
}

export function addNoteMessage(lineItems, documentType) {
    const sku = lineItems.map(item => item.sku).join(', ');
    return `${sku} added to ${documentType.toLowerCase()}`;
}

export function removeNoteMessage(lineItem, documentType) {
    return `${lineItem.sku} removed from ${documentType.toLowerCase()}`;
}

export function removeAllNoteMessage(lineItems, documentType) {
    const sku = filterRemoveAll(lineItems)
        .map(item => item.sku)
        .join(', ');
    return `${sku} removed from ${documentType.toLowerCase()}`;
}

export function approveAllNoteMessage(lineItems, documentType) {
    const sku = filterApproveAll(lineItems)
        .map(item => item.sku)
        .join(', ');
    return `${sku} approved on ${documentType.toLowerCase()}`;
}

export function updatedNoteMessage(originalLineItem, updatedLineItem) {
    if (originalLineItem.suppliedQuantity !== updatedLineItem.quantity) {
        return `${updatedLineItem.sku} updated quantity from ${updatedLineItem.quantity} to ${originalLineItem.suppliedQuantity}`;
    }

    return null;
}

export function initAdjustedPriceAdjustment(originalLineItem) {
    const { price } = originalLineItem;
    const adjustmentTypeId =
        price.adjustedPriceAdjustmentTypeId || price.priceAdjustmentTypeId;
    const adjustedPriceAdjustment =
        price.adjustedPriceAdjustment ?? price.priceAdjustment;

    if (adjustedPriceAdjustment !== null && isFinite(adjustedPriceAdjustment)) {
        if (adjustmentTypeId === 'MARKUP') {
            return Number((adjustedPriceAdjustment * 100).toFixed(2));
        }

        if (adjustmentTypeId === 'MARGIN') {
            return Number((adjustedPriceAdjustment * 100).toFixed(2));
        }

        return Number(adjustedPriceAdjustment);
    }

    return 0.0;
}

export function initAdjustedPriceAdjustmentFinance(originalLineItem) {
    // finance always uses a fixed price, so we just need to use original price
    const { price } = originalLineItem;
    return price?.adjustedPrice ?? 0;
}

export function getAdjustedPayout(updatedLineItem, originalLineItem) {
    return (
        updatedLineItem?.adjustedPayout ??
        (originalLineItem?.payout.adjustedPayout || 0)
    );
}

export function getAdjustedPrice(updatedLineItem, originalLineItem) {
    return (
        updatedLineItem?.adjustedPriceAdjustment ??
        initAdjustedPriceAdjustment(originalLineItem)
    );
}

export function getAdjustedPriceAdjustment(updatedLineItem, originalLineItem) {
    return (
        updatedLineItem?.adjustedPriceAdjustment ??
        ((originalLineItem?.price.adjustedPriceAdjustment ??
            originalLineItem?.price.priceAdjustment) ||
            0)
    );
}

export function getAdjustedPriceFinance(updatedLineItem, originalLineItem) {
    return (
        updatedLineItem?.adjustedPriceAdjustment ??
        initAdjustedPriceAdjustmentFinance(originalLineItem)
    );
}

export function getAdjustedPriceAdjustmentTypeId(
    updatedLineItem,
    originalLineItem
) {
    return (
        updatedLineItem?.adjustedPriceAdjustmentTypeId ||
        originalLineItem?.price.adjustedPriceAdjustmentTypeId ||
        originalLineItem?.price.priceAdjustmentTypeId ||
        'MARGIN'
    );
}

export function getAdjustedDetails(updatedLineItem, originalLineItem) {
    return (
        updatedLineItem?.adjustedDetails ||
        originalLineItem?.suppliedDescription ||
        originalLineItem?.details
    );
}

export function getFinancePriceAdjustment(totalPrice, cost, adjustmentTypeId) {
    if (adjustmentTypeId === 'MARKUP') {
        return extractMarkupFromTotalCost(totalPrice, cost);
    }

    if (adjustmentTypeId === 'MARGIN') {
        return extractMarginFromTotalCost(totalPrice, cost);
    }

    return totalPrice;
}

export function extractMarginFromTotalCost(totalPrice, originalCost) {
    return (totalPrice - originalCost) / totalPrice;
}

export function extractMarkupFromTotalCost(totalPrice, originalCost) {
    return (totalPrice - originalCost) / originalCost;
}

export function bulkAdjustNoteMessage(
    originalLineItems,
    updatedLineItems,
    adjustmentReason,
    adjustmentReasonNote
) {
    const constructedMessage = updatedLineItems.map(updatedLineItem => {
        const originalLineItem = originalLineItems.find(
            item => item.id === Number(updatedLineItem.id)
        );

        const adjustmentNote = [];

        const originalPayout = getAdjustedPayout(null, originalLineItem);
        const updatedPayout = getAdjustedPayout(
            updatedLineItem?.payout ?? updatedLineItem,
            originalLineItem
        );

        if (originalPayout !== updatedPayout) {
            adjustmentNote.push(
                `PAYOUT from $${originalPayout} to $${updatedPayout}`
            );
        }

        const priceText =
            updatedLineItem.productCategoryId === 'PART' ||
            updatedLineItem.productCategoryId === 'EQUIPMENT'
                ? 'PRICE ADJUSTMENTS'
                : 'PRICE';

        const originalAdjustedPrice = getAdjustedPriceAdjustment(
            null,
            originalLineItem
        );
        const originalAdjustedPriceType = getAdjustedPriceAdjustmentTypeId(
            null,
            originalLineItem
        );

        const updatedAdjustedPrice = getAdjustedPriceAdjustment(
            updatedLineItem?.price ?? updatedLineItem,
            originalLineItem
        );
        const updatedAdjustedPriceType = getAdjustedPriceAdjustmentTypeId(
            updatedLineItem?.price ?? updatedLineItem,
            originalLineItem
        );

        if (
            originalAdjustedPrice !== updatedAdjustedPrice ||
            originalAdjustedPriceType !== updatedAdjustedPriceType
        ) {
            const price = `${
                updatedAdjustedPriceType === 'FIXED'
                    ? `$${updatedAdjustedPrice}`
                    : `${(updatedAdjustedPrice * 100).toFixed(2)}%`
            }`;

            const originalPrice = `${
                originalAdjustedPriceType === 'FIXED'
                    ? `$${originalAdjustedPrice}`
                    : `${(originalAdjustedPrice * 100).toFixed(2)}%`
            }`;

            adjustmentNote.push(
                `${priceText} from ${originalAdjustedPriceType} ${originalPrice} to ${updatedAdjustedPriceType} ${price}`
            );
        }

        if (
            originalLineItem.cost.adjustedPriceTax !==
            updatedLineItem.cost.adjustedPriceTax
        ) {
            adjustmentNote.push(
                `TAX from $${originalLineItem.cost.adjustedPriceTax} to $${updatedLineItem.cost.adjustedPriceTax}`
            );
        }

        const originalDescription =
            originalLineItem.suppliedDescription || originalLineItem.details;
        if (
            updatedLineItem.suppliedDescription &&
            updatedLineItem.suppliedDescription !== originalDescription
        ) {
            adjustmentNote.push(
                `DETAILS from ${originalDescription} to ${updatedLineItem.suppliedDescription}`
            );
        }

        if (adjustmentNote.length > 0) {
            return `${updatedLineItem.sku} adjusted ${adjustmentNote.join(
                ', '
            )}`;
        }

        return null;
    });

    if (constructedMessage.length > 0) {
        return `${constructedMessage.join('\n\n')}  \n\nAdjustment reason: ${
            adjustmentReason.display
        }.${adjustmentReasonNote ? `\n\n ${adjustmentReasonNote}` : ``}`;
    }

    return null;
}

export function getCurrencyFromLineItems(lineItems) {
    if (!lineItems || lineItems.length === 0) {
        return;
    }
    const lineItem = lineItems.find(
        li =>
            li?.cost?.currency ||
            li?.price?.currency ||
            li?.payout?.currency ||
            li?.currency
    );
    if (!lineItem) {
        return;
    }
    return (
        lineItem.cost?.currency ||
        lineItem.price?.currency ||
        lineItem.payout?.currency ||
        lineItem?.currency
    );
}

/**
 * Get if line item is unavailable
 * @param {object} lineItem
 * @returns {boolean}
 */
export function isLineItemUnavailable(lineItem) {
    if (!lineItem) {
        return false;
    }
    const currency = getCurrencyFromLineItems([lineItem]);
    const country = getCountryFromCurrency(currency);
    return !!(
        !lineItem.product ||
        !country ||
        !isProductAvailable(lineItem.product, country)
    );
}

export function isBundleProductUnavailable(bundleProduct) {
    return (
        bundleProduct?.product?.productCategoryId === PRODUCT_CATEGORY.BUNDLE &&
        bundleProduct.product.bundleProducts.find(p => isLineItemUnavailable(p))
    );
}

export function isBundleLineItemUnavailable(bundleLineItem, lineItems) {
    if (bundleLineItem?.productCategoryId === PRODUCT_CATEGORY.BUNDLE) {
        return !!lineItems
            .filter(li => li.bundleJobLineItemId === bundleLineItem.id)
            .find(l => isLineItemUnavailable(l));
    }
    return false;
}

/**
 *
 * @param {object} lineItem
 * @param {number} lineItemId
 * @param {object[]} lineItems
 * @returns {object[]}
 */
export function adjustLineItem(lineItem, lineItemId, lineItems) {
    let updateParentBundledItem;

    // Parent of bundled items
    if (lineItem.bundleJobLineItemId) {
        const existingBundleLineItem = _.find(
            lineItems,
            l => l.id === lineItem.bundleJobLineItemId
        );
        const existingAdjustLineItem = _.find(
            lineItems,
            l => l.id === lineItemId
        );
        if (existingBundleLineItem) {
            const existingCost = _.round(
                existingAdjustLineItem.cost.cost *
                    existingAdjustLineItem.quantity,
                2
            );
            const updateCost = _.round(
                lineItem.cost.cost * lineItem.quantity,
                2
            );
            const updatedCost = _.round(
                existingBundleLineItem.cost.cost - existingCost + updateCost,
                2
            );
            if (updatedCost !== existingBundleLineItem.cost.cost) {
                updateParentBundledItem = {
                    ...existingBundleLineItem,
                    cost: {
                        ...existingBundleLineItem.cost,
                        cost: updatedCost,
                    },
                };
            }
        }
    }

    return _.map(lineItems, l => {
        // adjust item
        if (l.id === lineItemId) {
            return {
                ...l,
                ...{
                    ...lineItem,
                    updated: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
                },
            };
        }

        // parent item of adjust item
        if (updateParentBundledItem && updateParentBundledItem.id === l.id) {
            return {
                ...updateParentBundledItem,
                updated: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
            };
        }
        return l;
    });
}

export const resetBundleCostPriceToZero = (lineItems, bundleJobLineItemId) => {
    const itemsInBundle = lineItems.filter(
        li => li.bundleJobLineItemId === bundleJobLineItemId
    );
    const bundle = lineItems.find(li => li.id === bundleJobLineItemId);
    if (bundle && itemsInBundle.length === 0) {
        return lineItems.map(li =>
            li.id === bundle.id
                ? {
                      ...li,
                      cost: {
                          ...li.cost,
                          cost: 0,
                          adjustedPayout: 0,
                          adjustedPayoutAdjustment: 0,
                          adjustedPayoutAdjustmentTypeId: 'FIXED',
                          adjustedVendorCost: 0,
                          adjustedVendorCostAdjustment: 0,
                          adjustedVendorCostAdjustmentTypeId: 'FIXED',
                      },
                      price: {
                          ...li.price,
                          adjustedPrice: 0,
                          adjustedPriceAdjustment: 0,
                          adjustedPriceAdjustmentTypeId: 'FIXED',
                          adjustedPriceTax: 0,
                          adjustedPriceTaxPercent: 0,
                          adjustedPriceIncludingTax: 0,
                      },
                      updated: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
                  }
                : li
        );
    }
    return lineItems;
};

export const calculateTotalCost = (
    lineItem,
    adjustedPayout,
    adjustedCostAdjustment,
    adjustedPriceAdjustmentTypeId,
    adjustedPriceAdjustment
) => {
    const cost =
        lineItem.productCategoryId === 'LABOR' ||
        lineItem.productCategoryId === 'SUPPLIED'
            ? adjustedPayout
            : adjustedCostAdjustment;
    const markupType = adjustedPriceAdjustmentTypeId;
    let markupValue;
    let total;

    if (markupType !== 'FIXED') {
        markupValue = (adjustedPriceAdjustment || 0) / 100;
        total = calculate(markupType, cost, markupValue);

        return total;
    }
    markupValue = adjustedPriceAdjustment ?? cost;
    total = calculate(markupType, cost, markupValue);

    return total;
};

export const calculateGrossMargin = (
    lineItem,
    adjustedPayout,
    adjustedCostAdjustment,
    adjustedPriceAdjustmentTypeId,
    adjustedPriceAdjustment
) => {
    if (
        lineItem.productCategoryId === 'LABOR' ||
        lineItem.productCategoryId === 'SUPPLIED'
    ) {
        return (
            calculateTotalCost(
                lineItem,
                adjustedPayout,
                adjustedCostAdjustment,
                adjustedPriceAdjustmentTypeId,
                adjustedPriceAdjustment
            ) - adjustedPayout
        );
    }
    return (
        calculateTotalCost(
            lineItem,
            adjustedPayout,
            adjustedCostAdjustment,
            adjustedPriceAdjustmentTypeId,
            adjustedPriceAdjustment
        ) - adjustedCostAdjustment
    );
};

export const calculateMargin = (
    lineItem,
    adjustedPayout,
    adjustedCostAdjustment,
    adjustedPriceAdjustmentTypeId,
    adjustedPriceAdjustment,
    priceAdjustmentMode
) => {
    if (priceAdjustmentMode === 'MARGIN') {
        return adjustedPriceAdjustment || 0;
    }

    if (
        lineItem.productCategoryId === 'LABOR' ||
        lineItem.productCategoryId === 'SUPPLIED'
    ) {
        return calculateMarginPercentage(
            adjustedPayout,
            calculateTotalCost(
                lineItem,
                adjustedPayout,
                adjustedCostAdjustment,
                adjustedPriceAdjustmentTypeId,
                adjustedPriceAdjustment
            )
        );
    }
    return calculateMarginPercentage(
        adjustedCostAdjustment,
        calculateTotalCost(
            lineItem,
            adjustedPayout,
            adjustedCostAdjustment,
            adjustedPriceAdjustmentTypeId,
            adjustedPriceAdjustment
        )
    );
};

export const hasEquipmentLineItems = lineItems =>
    lineItems?.length > 0 && lineItems.some(lineItem => isEquipment(lineItem));

export const configureSelectedBundleLineItems = (bundle, lineItems) => {
    return lineItems.filter(item => item.bundleJobLineItemId === bundle.id);
};

export const initialSelectedLineItem = (lineItems, bundleLineItems) => {
    const firstLineItem = lineItems[0];
    const firstBundleLineItem = bundleLineItems[0];
    return firstLineItem || firstBundleLineItem;
};

export const displayApprove = (docStatus, docType) => {
    const validWoStatuses = [
        // 'SCHEDULING',
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.IN_REVIEW,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DISPATCHING,
    ];
    switch (docType) {
        case 'workOrders':
            return validWoStatuses.includes(docStatus);
        case 'quotes':
        case 'orders':
        default:
            return false;
    }
};

export const displayRemove = (docStatus, docType) => {
    const validWoStatuses = [
        // 'SCHEDULING',
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.IN_REVIEW,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.DISPATCHING,
    ];
    const validOrderStatuses = [ORDER_STATUS.CREATED, ORDER_STATUS.SUBMITTED];
    switch (docType) {
        case 'workOrders':
            return validWoStatuses.includes(docStatus);
        case 'orders':
            return validOrderStatuses.includes(docStatus);
        case 'quotes':
        default:
            return false;
    }
};

export const displayAdjust = (
    docStatus,
    docType,
    financeEdit,
    laborWarranty,
    apFinanceEdit = false
) => {
    const validWoStatuses = [
        WORK_ORDER_STATUS.CREATED,
        WORK_ORDER_STATUS.DRAFTED,
        WORK_ORDER_STATUS.SCHEDULING,
        WORK_ORDER_STATUS.CONTACT_REQUIRED,
        WORK_ORDER_STATUS.DISPATCHING,
        WORK_ORDER_STATUS.DISPATCHED,
        WORK_ORDER_STATUS.NO_CONTRACTOR_FOUND,
        WORK_ORDER_STATUS.DELAYED,
        WORK_ORDER_STATUS.IN_PROCESS,
        WORK_ORDER_STATUS.IN_REVIEW,
        WORK_ORDER_STATUS.SCHEDULED,
        WORK_ORDER_STATUS.ORDERED,
    ];
    const validWOFinanceEditStatuses = [
        WORK_ORDER_STATUS.CLOSED,
        WORK_ORDER_STATUS.COMPLETED,
    ];
    const validOrderStatuses = [
        ORDER_STATUS.CREATED,
        ORDER_STATUS.SUBMITTED,
        ORDER_STATUS.ORDER_REQUESTED,
    ];
    const validOrderFinanceEditStatuses = [
        ORDER_STATUS.CLOSED,
        ORDER_STATUS.COMPLETED,
    ];
    const validOrderLaborWarrantyStatuses = [ORDER_STATUS.CREATED];
    const validQuoteStatuses = [
        QUOTE_STATUS.CREATED,
        QUOTE_STATUS.DRAFTED,
        QUOTE_STATUS.REVISING,
        QUOTE_STATUS.SUBMITTED,
        QUOTE_STATUS.REVIEWED,
        QUOTE_STATUS.APPROVED,
    ];
    const validDraftStatuses = [DRAFT_STATUS.CREATED];

    switch (docType) {
        case 'workOrders':
            if (financeEdit) {
                return validWOFinanceEditStatuses.includes(docStatus);
            }
            return validWoStatuses.includes(docStatus);
        case 'quotes':
            return validQuoteStatuses.includes(docStatus);
        case 'orders':
            if (financeEdit) {
                return validOrderFinanceEditStatuses.includes(docStatus);
            }
            if (laborWarranty) {
                return validOrderLaborWarrantyStatuses.includes(docStatus);
            }
            return apFinanceEdit || validOrderStatuses.includes(docStatus);
        case 'Draft':
            return validDraftStatuses.includes(docStatus);
        case 'Template':
            return isMotiliOwner();
        case 'Bulk Create':
            return true;
        default:
            return false;
    }
};

export const displayManage = (docStatus, docType, laborWarranty) => {
    return (
        docType === 'orders' &&
        docStatus === ORDER_STATUS.CREATED &&
        laborWarranty
    );
};

export const getTaxAmount = lineItem => {
    return lineItem.tax?.totalTax ?? 0;
};

export const getInvoicePrice = (lineItem, isAdjustment) => {
    return isAdjustment ? -lineItem.price : lineItem.price;
};
export const getInvoicePriceSubTotal = (lineItem, isAdjustment) => {
    const subTotal = lineItem.quantity * lineItem.price;

    return isAdjustment ? -subTotal : subTotal;
};

export const getCreditSubTotal = lineItem => {
    const quantity = Math.abs(lineItem.quantity);
    return round(quantity * (lineItem.creditAmount || 0), 2);
};

export const filterApproveAll = lineItems => {
    return lineItems.filter(item => {
        if (
            item.statusId !== LINE_ITEM_STATUSES.CREATED ||
            item.bundleJobLineItemId
        ) {
            return false;
        }
        if (item.productCategoryId !== PRODUCT_CATEGORY.BUNDLE) {
            return true;
        }
        return !_.some(
            lineItems,
            _item =>
                _item.bundleJobLineItemId === item.id &&
                _item.statusId !== item.statusId
        );
    });
};

export const filterRemoveAll = lineItems => {
    return lineItems.filter(item => {
        if (
            [
                LINE_ITEM_STATUSES.COMPLETED,
                LINE_ITEM_STATUSES.RETURNING,
                LINE_ITEM_STATUSES.RETURNED,
                LINE_ITEM_STATUSES.PARTIALLY_RETURNED,
            ].includes(item.statusId) ||
            item.bundleJobLineItemId
        ) {
            return false;
        }
        if (item.productCategoryId !== PRODUCT_CATEGORY.BUNDLE) {
            return true;
        }
        return !_.some(
            lineItems,
            _item =>
                _item.bundleJobLineItemId === item.id &&
                _item.statusId !== item.statusId
        );
    });
};

export function calculateGainLoss(
    originPrice,
    originCost,
    originQuantity,
    newPrice,
    newCost,
    newQuantity
) {
    const originProfit = (originPrice - originCost) * originQuantity;
    const newProfit = (newPrice - newCost) * newQuantity;
    const diff = newProfit - originProfit;
    return round(diff, 2);
}
